import { cloneDeep } from 'lodash'
import React, { createContext, useReducer, useContext, ReactNode } from 'react'
import { TreatmentUpsellDetails } from '@customTypes/TreatmentUpsellDetails'
import { AdditionalAppointmentHints, vitaminDUpsellInformation } from '@customTypes/Booking'

type SetUpsell = {
  type: 'setUpsell'
  payload: TreatmentUpsellDetails
}

type SetCanTheoreticallyBeUpsold = {
  type: 'setCanTheoreticallyBeUpsold'
  payload: boolean
}

type ClearState = {
  type: 'clearState'
}

type SetUpsellInformation = {
  type: 'setUpsellInformation'
  payload: AdditionalAppointmentHints['upsellInformation'] | undefined
}

type Action = SetUpsell | SetCanTheoreticallyBeUpsold | ClearState | SetUpsellInformation

type State = {
  canTheoreticallyBeUpsold: boolean
  upsellDetails: TreatmentUpsellDetails | undefined
  upsellInformation?: AdditionalAppointmentHints['upsellInformation']
}

type Dispatch = (action: Action) => void

const UpsellContext = createContext<State | undefined>(undefined)
const UpsellDispatchContext = createContext<Dispatch | undefined>(undefined)

const defaultState: State = {
  canTheoreticallyBeUpsold: false,
  upsellDetails: undefined,
  upsellInformation: undefined,
}

const upsellReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'setUpsell': {
      return { ...state, upsellDetails: action.payload }
    }
    case 'setCanTheoreticallyBeUpsold':
      return { ...state, canTheoreticallyBeUpsold: action.payload }
    case 'setUpsellInformation':
      return {
        ...state,
        upsellInformation: action.payload,
      }
    case 'clearState':
      return cloneDeep(defaultState)
  }
}

interface UpsellProviderProps {
  children: ReactNode
}

export const UpsellProvider = ({ children }: UpsellProviderProps) => {
  const [state, dispatch] = useReducer(upsellReducer, { ...defaultState })

  return (
    <UpsellContext.Provider value={{ ...state }}>
      <UpsellDispatchContext.Provider value={dispatch}>{children}</UpsellDispatchContext.Provider>
    </UpsellContext.Provider>
  )
}

export const useUpsellContext = () => {
  const context = useContext(UpsellContext)
  if (context === undefined) {
    throw new Error('useUpsellState must be user within a UpsellProvider')
  }
  return context
}

export const useUpsellDispatch = () => {
  const context = useContext(UpsellDispatchContext)
  if (context === undefined) {
    throw new Error('useUpsellDispatch must be used within a UpsellProvider')
  }
  return context
}

export const setUpsell = (upsell: TreatmentUpsellDetails): SetUpsell => ({
  type: 'setUpsell',
  payload: upsell,
})

export const setCanTheoreticallyBeUpsold = (canTheoreticallyBeUpsold: boolean): SetCanTheoreticallyBeUpsold => ({
  type: 'setCanTheoreticallyBeUpsold',
  payload: canTheoreticallyBeUpsold,
})

export const clearState = (): ClearState => ({
  type: 'clearState',
})

export const setVitaminDUpsellInformation = (): SetUpsellInformation => ({
  type: 'setUpsellInformation',
  payload: vitaminDUpsellInformation,
})

export const clearVitaminDUpsellInformation = (): SetUpsellInformation => ({
  type: 'setUpsellInformation',
  payload: undefined,
})
